import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, CalendarBlank, Check, MagnifyingGlass } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";

export const letter = (i) => (i < 26 ? String.fromCharCode(65 + i) : String.fromCharCode(65 + Math.floor(i / 26) - 1) + String.fromCharCode(65 + (i % 26)));
const clean = (s) => String(s || "").replace(/\s+/g, " ");

export function ChoicePills({ options, value, onChange, testId }) {
  return (
    <div className="flex flex-col gap-2.5" role="radiogroup" data-testid={testId}>
      {options.map((o, i) => {
        const on = String(value) === String(o.value);
        return (
          <button
            key={o.value} type="button" role="radio" aria-checked={on} onClick={() => onChange(o.value)}
            className={`tsf-pill w-full min-h-[46px] flex items-center gap-3 rounded-lg border px-3 py-2 text-left text-[15px] ${on ? "border-[#1f64c2] bg-[#327de3]/20 shadow-[0_0_0_1px_#1f64c2] text-[#174f9c] font-semibold" : "border-[#327de3]/35 bg-[#327de3]/[0.07] text-[#1d5fbf] hover:bg-[#327de3]/[0.13]"}`}
            data-testid={`${testId}-opt-${i}`}
          >
            <span className={`shrink-0 inline-flex h-6 min-w-[24px] px-1 items-center justify-center rounded border text-[11px] font-bold ${on ? "bg-[#1f64c2] border-[#1f64c2] text-[#ffffff]" : "bg-[#ffffff] border-[#327de3]/40 text-[#1d5fbf]"}`}>{letter(i)}</span>
            <span className="flex-1">{clean(o.label)}</span>
            {on && <Check size={16} weight="bold" />}
          </button>
        );
      })}
    </div>
  );
}

export function EmployeeCombo({ names, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const list = useMemo(() => names.filter((n) => n.toLowerCase().includes(q.toLowerCase())), [names, q]);
  return (
    <div className="relative" data-testid="tsf-employee-combo">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="tsf-pill w-full h-12 flex items-center justify-between rounded-lg border border-[#327de3]/40 bg-[#ffffff] px-4 text-left text-[16px]"
        data-testid="tsf-employee-trigger">
        <span className={value ? "text-[#0f172a] font-medium" : "text-[#94a3b8]"}>{value || placeholder || "Type or select an option"}</span>
        <CaretDown size={16} className={`text-[#2f78dc] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="tsf-pop mt-2 rounded-xl border border-[#e2e8f0] bg-[#ffffff] shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 border-b border-[#f1f5f9] px-3">
            <MagnifyingGlass size={15} className="text-[#94a3b8]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name"
              className="tsf-search h-11 flex-1 bg-transparent text-[16px] outline-none" data-testid="tsf-employee-search" />
          </div>
          <div className="max-h-[42vh] overflow-y-auto p-2 flex flex-col gap-1.5">
            {list.map((n, i) => (
              <button key={n} type="button" onClick={() => { onChange(n); setOpen(false); setQ(""); }}
                className={`tsf-pill flex items-center gap-3 rounded-lg px-3 min-h-[44px] text-left text-[15px] ${value === n ? "bg-[#327de3]/20 text-[#174f9c] font-semibold" : "bg-[#327de3]/[0.06] text-[#1d5fbf] hover:bg-[#327de3]/[0.13]"}`}
                data-testid={`tsf-employee-opt-${i}`}>
                <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded border border-[#327de3]/40 bg-[#ffffff] text-[11px] font-bold">{letter(i)}</span>{n}
              </button>
            ))}
            {!list.length && <div className="px-3 py-4 text-sm text-[#94a3b8]">No matching name</div>}
          </div>
        </div>
      )}
    </div>
  );
}

const pad = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const realIso = (m, d, y) => {
  if (!/^\d{1,2}$/.test(m) || !/^\d{1,2}$/.test(d) || !/^\d{4}$/.test(y)) return null;
  const dt = new Date(+y, +m - 1, +d);
  return dt.getFullYear() === +y && dt.getMonth() === +m - 1 && dt.getDate() === +d ? isoOf(dt) : null;
};

export function DateField({ value, onChange, onPicked }) {
  const [parts, setParts] = useState(() => (value ? { m: value.slice(5, 7), d: value.slice(8, 10), y: value.slice(0, 4) } : { m: "", d: "", y: "" }));
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(value ? new Date(`${value}T12:00:00`) : undefined);
  const refs = { m: useRef(), d: useRef(), y: useRef() };
  useEffect(() => { onChange(realIso(parts.m, parts.d, parts.y) || (parts.m || parts.d || parts.y ? "invalid" : "")); }, [parts]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v, max, next) => {
    const s = v.replace(/\D/g, "").slice(0, max);
    setParts((p) => ({ ...p, [k]: s }));
    if (s.length === max && next) refs[next].current?.focus();
  };
  const seg = (k, ph, max, next, w) => (
    <input ref={refs[k]} inputMode="numeric" style={{ background: "transparent", color: "#0f172a" }} value={parts[k]} placeholder={ph} onChange={(e) => set(k, e.target.value, max, next)}
      className={`${w} bg-transparent border-b-2 border-[#327de3]/50 focus:border-[#1f64c2] text-center text-[22px] sm:text-[26px] text-[#0f172a] outline-none pb-1 placeholder:text-[#cbd5e1]`}
      data-testid={`tsf-date-${k}`} aria-label={ph} />
  );
  const confirm = () => {
    if (pick) setParts({ m: pad(pick.getMonth() + 1), d: pad(pick.getDate()), y: String(pick.getFullYear()) });
    setOpen(false);
  };
  return (
    <div className="relative">
      <div className="flex items-end gap-2">
        {seg("m", "MM", 2, "d", "w-14")}<span className="text-2xl text-[#cbd5e1] pb-1">/</span>
        {seg("d", "DD", 2, "y", "w-14")}<span className="text-2xl text-[#cbd5e1] pb-1">/</span>
        {seg("y", "YYYY", 4, null, "w-24")}
        <button type="button" onClick={() => setOpen((o) => !o)} className="ml-2 mb-1 h-10 w-10 inline-flex items-center justify-center rounded-lg text-[#2f78dc] hover:bg-[#327de3]/10" data-testid="tsf-date-calendar-btn" aria-label="Open calendar">
          <CalendarBlank size={24} weight="bold" />
        </button>
      </div>
      {open && (
        <div className="tsf-pop absolute z-20 left-0 top-full mt-3 rounded-2xl border border-[#e2e8f0] bg-[#ffffff] p-2 shadow-xl" data-testid="tsf-date-popover">
          <Calendar mode="single" selected={pick} onSelect={(d) => {
            setPick(d);
            if (!d) return;
            setParts({ m: pad(d.getMonth() + 1), d: pad(d.getDate()), y: String(d.getFullYear()) });
            setOpen(false);
            onPicked?.(isoOf(d));
          }} defaultMonth={pick || new Date()} />
          <div className="flex items-center justify-between px-2 pb-1">
            <button type="button" onClick={() => { setPick(undefined); setParts({ m: "", d: "", y: "" }); }} className="rounded-full border border-[#e2e8f0] px-4 h-9 text-sm text-[#475569] hover:bg-[#f8fafc]" data-testid="tsf-date-reset">Reset</button>
            <button type="button" onClick={confirm} className="h-10 w-10 rounded-full bg-[#327de3] text-[#ffffff] inline-flex items-center justify-center hover:bg-[#1f64c2]" data-testid="tsf-date-confirm" aria-label="Confirm date"><Check size={18} weight="bold" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

export function TextAnswer({ kind, value, onChange }) {
  return (
    <input style={{ background: "transparent", color: "#0f172a" }} value={value || ""} onChange={(e) => onChange(e.target.value)} inputMode={kind === "number" ? "decimal" : "text"} placeholder="Type your answer here..."
      className="w-full bg-transparent border-b-2 border-[#327de3]/50 focus:border-[#1f64c2] text-[20px] text-[#0f172a] outline-none pb-2 placeholder:text-[#cbd5e1]" data-testid="tsf-text-answer" />
  );
}
